import { IdName } from "@/types/common-types";
import { PersonType } from "@/types/person-types";
import { gql } from "@apollo/client";
import ComponentState, { ComponentStateTypes } from "components/common/ComponentState";
import Form from "components/common/form/Form";
import CheckboxInput from "components/common/form/inputs/CheckboxInput";
import Input from "components/common/form/inputs/Input";
import Textarea from "components/common/form/inputs/Textarea";
import DateInput from "components/common/form/inputs/date/DateInput";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import FormActions from "components/common/form/partials/FormActions";
import { api, graph } from "config/axios-context";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EventFormDataQuery, EventFormDataQueryVariables } from "types/graphql";
import { DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import PersonDropdown from "../../app/person/partials/inputs/PersonDropdown";

const EVENT_REPEAT_TYPES = [
    { enumId: 0, id: 'daily', name: 'calendar.day.continiusly' },
    { enumId: 1, id: 'weekly', name: 'calendar.week.continiusly' },
    { enumId: 2, id: 'monthly', name: 'calendar.month.continiusly' },
    { enumId: 3, id: 'yearly', name: 'calendar.year.continiusly' }
];

type BookingEventInput = {
    id?: number;
    bookingId?: number | null;
    date?: Date | null;
    isDone: boolean;
    name: string;
    note?: string;
    order: number | string;
    repeat: boolean;
    repeatUntil?: Date | null;
    repeatEvery?: number | null;
    repeatType?: typeof EVENT_REPEAT_TYPES[number];
    assignee?: IdName | null;
}

type EventFormPropsType = {
    modelId?: number | null;
    onSave: () => void;
    name: string,
    bookingId?: number | null;
}


const query = gql`
query EventFormData($id: Int, $bookingId: Int!) {
    bookingEvent(id: $id) {
      bookingId
      isDone
      date
      note
      name
      id
      order
      group,
      assigneeId,
      assignee {
        id
        name
      }
      booking {
        id
        inDate
        outDate
      }
    }
    booking(id: $bookingId) {
      id
      inDate
      outDate
    }
  }  
`;


const EventForm = (props: EventFormPropsType) => {
    const context = useAppContext();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    const [disableActions, setDisableActions] = useState<boolean>(false);
    const [bookingData, setBookingData] = useState<EventFormDataQuery['booking']>(null);
    const [t] = useTranslation();
    const [model, setModel] = useState<BookingEventInput>({
        id: props.modelId || 0,
        name: '',
        note: '',
        bookingId: props.bookingId,
        date: null,
        isDone: false,
        order: 10,
        repeat: false,
        repeatUntil: null,
        repeatEvery: 1,
        repeatType: EVENT_REPEAT_TYPES[0],
        assignee: null
    });

    const [dateRange, setDateRange] = useState<Date[]>([]);

    // #region load data
    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        setComponentState('loading');
        try {
            const response = await graph<EventFormDataQuery, EventFormDataQueryVariables>({
                query,
                variables: {
                    id: Number(props.modelId || 0),
                    bookingId: model.bookingId || 0
                }
            });

            if (response.errors) {
                throw response.errors;
            }

            const theModel = {
                ...model
            }

            if (response.data?.booking) {
                const bookingData = response.data?.booking || response.data?.bookingEvent?.booking;
                setDateRange(DateUtil.addMissingDates([new Date(bookingData!.inDate), new Date(bookingData!.outDate)]));
                theModel.repeatUntil = new Date(bookingData.outDate);
            }

            if (response.data?.bookingEvent) {
                setModel({
                    isDone: response.data.bookingEvent.isDone,
                    date: response.data.bookingEvent.date ? new Date(response.data.bookingEvent.date) : null,
                    id: response.data.bookingEvent.id,
                    name: response.data.bookingEvent.name,
                    note: response.data.bookingEvent.note || '',
                    order: response.data.bookingEvent.order,
                    bookingId: response.data.bookingEvent.bookingId || response.data.bookingEvent.booking?.id,
                    repeat: false, // Default to false, can be set based on requirements
                    repeatUntil: response.data?.booking?.outDate ? new Date(response.data.booking!.outDate) : null,
                    repeatEvery: 1,
                    repeatType: EVENT_REPEAT_TYPES[0],
                    assignee: response.data.bookingEvent.assignee
                });
            } else {
                setModel({
                    ...theModel,    
                });
            }

            setComponentState('');
        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    }
    // #endregion load data

    // #region save data
    const saveData = async () => {
        setDisableActions(true);
        try {
            const response = await api.post<{ id: number }>(props.modelId ? `BookingEvent/Save` : `BookingEvent/Create`, {
                date: DateUtil.toIsoDate(model.date!),
                id: model.id,
                isDone: model.isDone,
                name: model.name,
                note: model.note,
                order: model.order,
                bookingId: model.bookingId,
                repeat: model.repeat ? {
                    every: model.repeatEvery,
                    repeatType: model.repeatType?.enumId,
                    until: model.repeatUntil ? DateUtil.toIsoDate(model.repeatUntil) : null
                } : undefined,
            });

        if (!response.success) {
            throw "Could not save event: " + response.statusText;
        }

        if (props.onSave) {
            props.onSave();
        }
        setDisableActions(true);
    } catch (error: any) {
        context.notify('error', 'form.submit.error', Array.isArray(error) ? error[0].message : error.message);
        throw error;
    } finally {
        setDisableActions(false);
    }
}

return (
    <ComponentState state={componentState}>
        <Form name={props.name} onSubmit={saveData} >
            <fieldset>
                <Input
                    name="name"
                    label="fields.name.name"
                    value={model.name}
                    onChange={(value) => setModel({ ...model, name: value || '' })}
                    required
                />
                <DateInput
                    name="date"
                    label="fields.date.name"
                    value={(model.date && new Date(model.date)) || undefined}
                    required
                    highlightDates={dateRange}
                    viewDate={dateRange && dateRange[0]}
                    onChange={(value) => setModel({ ...model, date: value || null })}
                />
                <PersonDropdown
                    name="assignee"
                    label="fields.assignee.name"
                    value={(model.assignee as PersonType) || undefined}
                    onChange={(value) => setModel({ ...model, assignee: value || null })}
                />
                <CheckboxInput
                    name="isDone"
                    label="fields.is-done.name"
                    value={model.isDone}
                    onChange={(value) => setModel({ ...model, isDone: value || false })}
                />
                <CheckboxInput
                    name="repeat"
                    label="fields.repeat.name"
                    value={model.repeat}
                    disabled={props.modelId ? true : false}
                    onChange={(value) => setModel({ ...model, repeat: value || false })}
                />

                {model.repeat && (<div className="border-y bg-gray-100 p-2 dark:bg-neutral-900">
                    <Input
                        name="repeatEvery"
                        type="number"
                        step={1}
                        required={model.repeat}
                        label="fields.repeat-every.name"
                        value={model.repeatEvery?.toString() || ''}
                        onChange={(value) => setModel({ ...model, repeatEvery: value ? parseInt(value) : null })}
                    />
                    <DropdownInput
                        name="repeatType"
                        label="fields.repeat-on.name"
                        required={model.repeat}
                        dataSource={{
                            data: EVENT_REPEAT_TYPES
                        }}
                        getItemId={(i => i.id)}
                        view={{
                            inputView: (i) => i ? t(i.name) : '',
                            dropdownView: (i) => t(i.name)
                        }}
                        value={model.repeatType}
                        onChange={(value) => setModel({ ...model, repeatType: value || EVENT_REPEAT_TYPES[0] })}
                    />
                    <DateInput
                        name="repeatUntil"
                        label="fields.repeat-until.name"
                        highlightDates={dateRange}
                        min={model.date || undefined}
                        viewDate={dateRange && dateRange[0]}
                        value={model.repeatUntil ? new Date(model.repeatUntil) : undefined}
                        onChange={(value) => setModel({ ...model, repeatUntil: value || null })}
                    />
                </div>)}

                <Input
                    name="order"
                    type="number"
                    step={0}
                    required
                    label="fields.order.name"
                    value={model.order.toString()}
                    onChange={v => setModel({ ...model, order: v || '' })}
                />
                <Textarea
                    name="note"
                    label="fields.note.name"
                    value={model.note || ''}
                    onChange={(value) => setModel({ ...model, note: value })}
                />
            </fieldset>
            <FormActions disableInputs={disableActions} />
        </Form>
    </ComponentState>
);
};

export default EventForm;
